import { all, takeEvery, takeLatest, put, call, select } from 'redux-saga/effects';
import actions from './actions';
import { tableConfig } from '../../../settings/index';
import { sendRequest } from '../../../helpers/saga';
import { logger } from '../../../helpers/logger';
import notifications from '../../../redux/notifications/actions';
import Api, { executeAction } from '../../../helpers/api';
import { t } from '../../../i18n';

const HTTP_OK = 200;
const HTTP_MULTIPLE_CHOICES = 300;

const legacyApi = new Api('agencies/invites');
const agencyContractsApi = new Api('agency_contracts/invites');

// `sendRequest` toasts 403s and network errors itself and hands back `undefined`; a 4xx with a validations
// map comes back bare and silent, so its first sentence is surfaced here.
const refusalNotification = result =>
  notifications.addNotification({
    headline: t('dash.common.notification.error_headline'),
    type: 'error',
    message: result?.base?.[0] ?? result?.messages?.[0] ?? t('dash.common.notification.something_went_wrong'),
  });

function* loadData({ payload }) {
  try {
    const { page = 1, pageSize = tableConfig.pageSize, column, order, filter = '' } = payload;

    // The contracts endpoint is the only one that lists engine-bound drafts, and a draft has no
    // legal entity to gate on — so the pricing flag decides this as much as LegalEntities does.
    const useAgencyContracts = yield select(state =>
      Boolean(
        state.StartAppSettings?.feature_flags?.legal_entity_enabled ||
          state.StartAppSettings?.feature_flags?.pricing_configuration_enabled,
      ),
    );
    const api = useAgencyContracts ? agencyContractsApi : legacyApi;

    const result = yield call(
      sendRequest,
      api.load,
      { search: { ...payload, page, pageSize, column, order, filter } },
      notifications.REQUEST_ERROR,
    );

    if (result?.data?.success?.data) {
      const { records, filteredCount, filters, sortableColumns, totalCount } = result.data.success.data;

      yield put(
        actions.loadDataSuccess({
          data: records,
          filteredTotal: filteredCount,
          filters,
          sortableColumns,
          total: totalCount,
        }),
      );

      return;
    }

    if (result) {
      yield put(refusalNotification(result));
    }

    yield put(actions.loadDataError());
  } catch (error) {
    logger.error(error);

    yield put(actions.loadDataError());
  }
}

function* reinviteAction({ payload }) {
  try {
    const { method, url } = payload.action;
    const { id } = payload.record;
    const result = yield call(sendRequest, executeAction, { url, method }, notifications.REQUEST_ERROR);

    // On a refusal `sendRequest` hands back the bare validations map — truthy, but with no status — so a
    // truthiness check would report an invite that was never sent.
    if (result?.status >= HTTP_OK && result.status < HTTP_MULTIPLE_CHOICES) {
      yield put(actions.reinviteActionSuccess({ id }));
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.agencies.invite.agency_re-invited_notification'),
        }),
      );
    } else if (result) {
      yield put(refusalNotification(result));
    }
  } catch (error) {
    logger.error(error);

    yield put(refusalNotification());
  }
}

export default function* rootSaga() {
  // `LOAD_ERROR` blanks the rows, so a slow failed load must not outlive a newer successful one.
  yield all([takeLatest(actions.LOAD_REQUEST, loadData), takeEvery(actions.REINVITE_ACTION_REQUEST, reinviteAction)]);
}
